import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, AlertCircle, KeyRound, CheckCircle2, ShieldCheck } from "lucide-react";

const LOGO_SRC = "/fundio-logo-dark.png";
const inputClass =
  "w-full rounded-lg border border-fundio-line bg-fundio-surface px-4 text-sm text-fundio-cream outline-none transition-colors placeholder:text-fundio-slate focus:border-fundio-gold-bright focus:ring-1 focus:ring-fundio-gold-bright/40";

export default function SetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";

  const [state, setState] = useState("checking"); // checking | invalid | form | done
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) { setState("invalid"); return; }
    api
      .get(`/auth/password-setup/validate`, { params: { token } })
      .then(({ data }) => {
        if (!active) return;
        if (data?.valid) { setInfo(data); setState("form"); } else { setState("invalid"); }
      })
      .catch(() => active && setState("invalid"));
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 10) { setError("Das neue Passwort muss mindestens 10 Zeichen haben."); return; }
    if (pw !== confirm) { setError("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      await api.post(`/auth/password-setup/complete`, { token, new_password: pw });
      setPw(""); setConfirm("");
      window.history.replaceState({}, "", "/passwort-setzen");
      setState("done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen w-full bg-fundio-dark text-fundio-cream">
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="fundio-reveal" style={{ width: 384, maxWidth: "calc(100vw - 48px)" }}>
          <div className="mb-8 flex justify-center">
            <img src={LOGO_SRC} alt="Fundio" draggable="false" data-testid="setpw-logo"
              className="object-contain" style={{ width: 200, height: "auto" }} />
          </div>

          <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-fundio-gold/40 bg-fundio-gold-bright/10 px-3 py-1 text-fundio-gold-bright"
            style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.04em" }}>
            <KeyRound className="h-3.5 w-3.5" /> PERSÖNLICHER EINMAL-LINK
          </div>

          {state === "checking" && (
            <div data-testid="setpw-checking" className="mt-6 flex items-center gap-2 text-sm text-fundio-mist">
              <Loader2 className="h-4 w-4 animate-spin text-fundio-gold-bright" /> Link wird geprüft …
            </div>
          )}

          {state === "invalid" && (
            <div data-testid="setpw-invalid" className="mt-6">
              <h1 className="font-heading text-fundio-cream" style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.08 }}>
                Link ungültig oder abgelaufen
              </h1>
              <p className="mt-3 text-sm text-fundio-mist">
                Dieser Einmal-Link ist nicht mehr gültig. Bitte fordern Sie bei Ihrem Super-Admin einen neuen,
                persönlichen Link an.
              </p>
              <button data-testid="setpw-to-login" onClick={() => navigate("/login")}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg border border-fundio-gold bg-fundio-surface font-sans text-fundio-cream transition-all hover:border-fundio-gold-highlight"
                style={{ height: 48, fontSize: 14, fontWeight: 500 }}>
                Zur Anmeldung
              </button>
            </div>
          )}

          {state === "form" && (
            <form onSubmit={submit} data-testid="setpw-form" autoComplete="off">
              <h1 className="font-heading text-fundio-cream" style={{ marginTop: 14, fontSize: 26, fontWeight: 800, lineHeight: 1.08 }}>
                Passwort festlegen
              </h1>
              <p className="font-sans text-fundio-mist" style={{ marginTop: 6, fontSize: 13, lineHeight: 1.5 }}>
                Konto <span data-testid="setpw-email" className="font-semibold text-fundio-cream">{info?.masked_email}</span>.
                Legen Sie Ihr persönliches Passwort fest. Danach melden Sie sich normal an und richten die
                Zwei-Faktor-Authentifizierung (2FA) ein.
              </p>

              <label className="block font-sans text-fundio-mist" style={{ marginTop: 20, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Neues Passwort
              </label>
              <input data-testid="setpw-new-input" required type="password" value={pw}
                onChange={(e) => setPw(e.target.value)} placeholder="••••••••••" autoComplete="new-password"
                className={inputClass} style={{ height: 48, marginTop: 6 }} />

              <label className="block font-sans text-fundio-mist" style={{ marginTop: 16, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Passwort bestätigen
              </label>
              <input data-testid="setpw-confirm-input" required type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••••" autoComplete="new-password"
                className={inputClass} style={{ height: 48, marginTop: 6 }} />

              <p className="mt-2 text-[11px] text-fundio-slate">Mindestens 10 Zeichen.</p>

              {error && (
                <div data-testid="setpw-error" className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300" style={{ marginTop: 14 }}>
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span>
                </div>
              )}

              <button type="submit" data-testid="setpw-submit-button" disabled={loading || pw.length < 10 || confirm.length < 10}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-fundio-gold bg-fundio-surface font-sans text-fundio-cream transition-all hover:border-fundio-gold-highlight hover:shadow-[0_0_18px_-4px_rgba(231,181,21,0.5)] disabled:opacity-40"
                style={{ height: 48, marginTop: 18, fontSize: 14, fontWeight: 500 }}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Passwort festlegen"}
              </button>
            </form>
          )}

          {state === "done" && (
            <div data-testid="setpw-done" className="mt-6">
              <div className="flex items-center gap-2 text-fundio-gold-bright">
                <CheckCircle2 className="h-5 w-5" />
                <h1 className="font-heading text-fundio-cream" style={{ fontSize: 24, fontWeight: 800 }}>Passwort gesetzt</h1>
              </div>
              <p className="mt-3 text-sm text-fundio-mist">
                Ihr Passwort wurde festgelegt. Melden Sie sich jetzt normal an — beim ersten Login richten Sie
                die Zwei-Faktor-Authentifizierung (2FA) ein.
              </p>
              <button data-testid="setpw-done-to-login" onClick={() => navigate("/login")}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg border border-fundio-gold bg-fundio-surface font-sans text-fundio-cream transition-all hover:border-fundio-gold-highlight"
                style={{ height: 48, fontSize: 14, fontWeight: 500 }}>
                Zur Anmeldung
              </button>
            </div>
          )}

          <p className="flex items-center gap-1.5 font-sans text-fundio-slate" style={{ marginTop: 24, fontSize: 12 }}>
            <ShieldCheck className="h-3.5 w-3.5 text-fundio-gold-bright" /> Persönlicher Link — bitte nicht weiterleiten.
          </p>
        </div>
      </div>
    </div>
  );
}
